'use client';

import React from 'react';
import { Copy, Download, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface TraditionalClinicalNoteProps {
  sessionId?: string;
  patientName?: string;
  age?: string;
  gender?: string;
  department?: string;
  phone?: string;
  recordNumber?: string;
  doctorName?: string;
  content?: string;
  onContentChange?: (content: string) => void;
  className?: string;
}

/**
 * Traditional clinical note component - mimics hospital outpatient record format
 * Designed for printing and formal documentation
 */
export function TraditionalClinicalNote({
  sessionId = '',
  patientName = '匿名患者',
  age = '',
  gender = '',
  department = '心理科',
  phone = '',
  recordNumber = '',
  doctorName = '',
  content = '',
  onContentChange,
  className = '',
}: TraditionalClinicalNoteProps) {
  
  const getCurrentDate = () => {
    const now = new Date();
    return `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('已复制到剪贴板');
    } catch (err) {
      toast.error('复制失败');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `门诊病历-${patientName}-${sessionId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('病历已下载');
  };

  // Parse content into sections based on markdown headers
  const sections = React.useMemo(() => {
    const lines = content.split('\n');
    const sectionData: { [key: string]: string[] } = {
      '基本信息': [],
      '躯体检查': [],
      '精神检查': [],
      '辅助检查': [],
      '初步诊断及拟诊讨论': [],
      '诊疗计划': [],
    };
    
    let currentSection = '';
    
    for (const line of lines) {
      const lowerLine = line.toLowerCase();
      
      // Match section headers - support multiple formats
      if (
        line.includes('病史采集') || 
        line.includes('一、') || 
        lowerLine.includes('chief complaint') || 
        line.includes('主诉') ||
        lowerLine.includes('history of present illness') ||
        line.includes('现病史') ||
        line.includes('一般资料') ||
        line.includes('个人史') ||
        line.includes('家族史') ||
        line.includes('既往史')
      ) {
        currentSection = '基本信息';
      } else if (
        line.includes('躯体检查') || 
        line.includes('二、') ||
        lowerLine.includes('physical examination') ||
        line.includes('体格检查')
      ) {
        currentSection = '躯体检查';
      } else if (
        line.includes('精神检查') || 
        line.includes('三、') ||
        lowerLine.includes('mental status examination') ||
        line.includes('精神状态检查') ||
        line.includes('精神状态') ||
        line.includes('外表与行为') ||
        line.includes('言谈与思维') ||
        line.includes('情绪状态') ||
        line.includes('感知') ||
        line.includes('认知功能') ||
        line.includes('自知力')
      ) {
        currentSection = '精神检查';
      } else if (
        line.includes('辅助检查') || 
        line.includes('四、') ||
        lowerLine.includes('auxiliary examination') ||
        line.includes('实验室检查') ||
        line.includes('量表')
      ) {
        currentSection = '辅助检查';
      } else if (
        line.includes('诊断') || 
        line.includes('五、') ||
        lowerLine.includes('diagnosis') ||
        line.includes('初步评估') ||
        line.includes('鉴别诊断') ||
        line.includes('拟诊讨论')
      ) {
        currentSection = '初步诊断及拟诊讨论';
      } else if (
        line.includes('诊疗计划') || 
        line.includes('六、') ||
        lowerLine.includes('treatment plan') ||
        line.includes('治疗方案') ||
        line.includes('处理意见') ||
        line.includes('建议计划')
      ) {
        currentSection = '诊疗计划';
      } else if (currentSection && line.trim() && !line.startsWith('#') && !line.startsWith('---')) {
        sectionData[currentSection].push(line);
      }
    }
    
    return sectionData;
  }, [content]);

  return (
    <div className={`traditional-note-container ${className}`}>
      {/* Action buttons - only visible on screen, hidden when printing */}
      <div className="no-print mb-4 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleCopy}>
          <Copy className="w-4 h-4 mr-1" />
          复制
        </Button>
        <Button variant="outline" size="sm" onClick={handleDownload}>
          <Download className="w-4 h-4 mr-1" />
          下载
        </Button>
        <Button variant="default" size="sm" onClick={handlePrint}>
          <Save className="w-4 h-4 mr-1" />
          打印
        </Button>
      </div>

      {/* Traditional clinical note format */}
      <div className="traditional-note bg-white p-8 border border-slate-300">
        {/* Header - Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-2">门诊病历</h1>
        </div>

        {/* Patient Information Table */}
        <table className="w-full border-collapse mb-6 text-sm">
          <tbody>
            <tr>
              <td className="border border-slate-400 px-3 py-2 font-medium bg-slate-50 w-16">姓名</td>
              <td className="border border-slate-400 px-3 py-2 w-1/4">{patientName}</td>
              <td className="border border-slate-400 px-3 py-2 font-medium bg-slate-50 w-16">年龄</td>
              <td className="border border-slate-400 px-3 py-2 w-1/4">{age}</td>
              <td className="border border-slate-400 px-3 py-2 font-medium bg-slate-50 w-16">性别</td>
              <td className="border border-slate-400 px-3 py-2">{gender}</td>
            </tr>
            <tr>
              <td className="border border-slate-400 px-3 py-2 font-medium bg-slate-50">编号</td>
              <td className="border border-slate-400 px-3 py-2">{recordNumber || sessionId}</td>
              <td className="border border-slate-400 px-3 py-2 font-medium bg-slate-50">科室</td>
              <td className="border border-slate-400 px-3 py-2">{department}</td>
              <td className="border border-slate-400 px-3 py-2 font-medium bg-slate-50">电话</td>
              <td className="border border-slate-400 px-3 py-2">{phone}</td>
            </tr>
          </tbody>
        </table>

        {/* Clinical Sections */}
        <div className="space-y-4 text-sm leading-relaxed">
          {/* Section 1: Basic Information */}
          <div className="section">
            <h2 className="font-bold mb-2">一、基本信息：</h2>
            <div className="pl-6 whitespace-pre-wrap">
              {sections['基本信息'].join('\n') || '（待填写）'}
            </div>
          </div>

          {/* Section 2: Physical Examination */}
          <div className="section">
            <h2 className="font-bold mb-2">二、躯体检查：</h2>
            <div className="pl-6 whitespace-pre-wrap">
              {sections['躯体检查'].join('\n') || '（待填写）'}
            </div>
          </div>

          {/* Section 3: Mental Status Examination */}
          <div className="section">
            <h2 className="font-bold mb-2">三、精神检查：</h2>
            <div className="pl-6 whitespace-pre-wrap">
              {sections['精神检查'].join('\n') || '（待填写）'}
            </div>
          </div>

          {/* Section 4: Auxiliary Examination */}
          <div className="section">
            <h2 className="font-bold mb-2">四、辅助检查：</h2>
            <div className="pl-6 whitespace-pre-wrap">
              {sections['辅助检查'].join('\n') || '（待填写）'}
            </div>
          </div>

          {/* Section 5: Preliminary Diagnosis */}
          <div className="section">
            <h2 className="font-bold mb-2">五、初步诊断及拟诊讨论：</h2>
            <div className="pl-6 whitespace-pre-wrap">
              {sections['初步诊断及拟诊讨论'].join('\n') || '（待填写）'}
            </div>
          </div>

          {/* Section 6: Treatment Plan */}
          <div className="section">
            <h2 className="font-bold mb-2">六、诊疗计划：</h2>
            <div className="pl-6 whitespace-pre-wrap">
              {sections['诊疗计划'].join('\n') || '（待填写）'}
            </div>
          </div>
        </div>

        {/* Footer - Signature */}
        <div className="mt-12 flex justify-between items-end text-sm">
          <div className="flex items-end space-x-2">
            <span className="font-medium">医生签名：</span>
            {/* Electronic signature image */}
            <div className="signature-container">
              <img 
                src="/signature-removebg-zoom.png" 
                alt="医生签名" 
                className="signature-image"
              />
            </div>
            {/* <span className="ml-2 signature-name">{doctorName || '治春无'}</span> */}
          </div>
          <div>
            <span className="font-medium">时间：</span>
            <span className="ml-2">{getCurrentDate()}</span>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          /* Page border for printing */
          .traditional-note {
            box-shadow: none !important;
            /* Keep the border visible during print */
            border: 2px solid #334155 !important;
            padding: 1.5cm !important;
            page-break-inside: avoid;
          }
          
          @page {
            size: A4;
            margin: 1.5cm;
          }
          
          /* Ensure border appears on every page */
          .traditional-note-container {
            position: relative;
          }
          
          /* Signature image sizing for print */
          .signature-image {
            max-height: 40px !important;
            width: auto !important;
          }
        }
        
        @media screen {
          /* Border for screen display */
          .traditional-note {
            border: 2px solid #cbd5e1;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
          
          .signature-image {
            max-height: 50px;
            width: auto;
          }
        }
        
        .traditional-note {
          font-family: 'SimSun', '宋体', serif;
          line-height: 1.8;
          position: relative;
        }
        
        .traditional-note h1 {
          font-family: 'SimHei', '黑体', sans-serif;
        }
        
        .traditional-note h2 {
          font-family: 'SimHei', '黑体', sans-serif;
        }
        
        /* Signature container */
        .signature-container {
          display: inline-block;
          vertical-align: bottom;
        }
        
        .signature-image {
          display: block;
          object-fit: contain;
          filter: grayscale(0%);
        }
        
        .signature-name {
          font-style: normal;
          color: #475569;
        }
      `}</style>
    </div>
  );
}

export default TraditionalClinicalNote;

